import React, { FC, useState, useEffect, useCallback, useLayoutEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./DigitSpan.module.css";
import { ArrowBack } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { getUserId } from "../../utils/userId";
import { v4 as uuidv4 } from "uuid";
import { doc, serverTimestamp, setDoc, Timestamp } from "@firebase/firestore";
import { db } from "../../utils/firebase";
import {
  generateTestId,
  getCognitiveTestId,
  setCognitiveTestId,
  getCognitiveTestSetId,
} from "../../utils/testId";
// --- Types ---
type TestPhase = "idle" | "presenting" | "input" | "feedback" | "finished";
type TestMode = "forward" | "backward";

interface TestResult {
  sequence: number[];
  userInput: number[];
  correct: boolean;
  mode: TestMode;
}

// --- Component ---
const DigitSpanTest: FC = () => {
  const [testPhase, setTestPhase] = useState<TestPhase>("idle");
  const [currentMode, setCurrentMode] = useState<TestMode>("forward");
  const [sequenceToRemember, setSequenceToRemember] = useState<number[]>([]);
  const [userSequenceInput, setUserSequenceInput] = useState<number[]>([]);
  const [displayIndex, setDisplayIndex] = useState<number>(-1);
  const [results, setResults] = useState<TestResult[]>([]);
  const [currentLevel, setCurrentLevel] = useState<number>(3);
  const [trialInLevel, setTrialInLevel] = useState<number>(0);
  const [totalSuccess, setTotalSuccess] = useState<number>(0);
  const [finalSpanLength, setFinalSpanLength] = useState<number>(0);
  const [currentKeypadArrangement, setCurrentKeypadArrangement] = useState<
    number[]
  >([]);
  const [currentKeypadColors, setCurrentKeypadColors] = useState<string[]>([]);
  const [isDisplayingDigit, setIsDisplayingDigit] = useState(false);
  const [isAnimatingInput, setIsAnimatingInput] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hospitalId } = useParams();
  const prefix = hospitalId ? `/${hospitalId}` : '';

  const COLORS = [
    "#FC8AFF",
    "#C286FF",
    "#FFDD17",
    "#4CDAFE",
    "#ED0F29",
    "#FF60D2",
  ];
  const PRESENTATION_TIME = 1000;

  const handleBack = () => navigate(`${prefix}/cognitive/check/new`);

  const generateRandomSequence = (length: number): number[] => {
    const seq: number[] = [];
    let last = -1;
    for (let i = 0; i < length; i++) {
      let num;
      do {
        num = Math.floor(Math.random() * 9) + 1;
      } while (num === last);
      seq.push(num);
      last = num;
    }
    return seq;
  };

  const shuffleArray = <T,>(array: T[]): T[] => {
    const arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  const generateRandomColors = () =>
    setCurrentKeypadColors(shuffleArray(COLORS));
  const generateNewKeypadArrangement = useCallback(() => {
    setCurrentKeypadArrangement(shuffleArray([1, 2, 3, 4, 5, 6, 7, 8, 9]));
    setCurrentKeypadColors(shuffleArray(COLORS));
  }, []);

  const startNewTest = useCallback(() => {
    setCurrentLevel(3);
    setTrialInLevel(0);
    setTotalSuccess(0);
    setResults([]);
    setFinalSpanLength(0);
    setUserSequenceInput([]);
    setSequenceToRemember(generateRandomSequence(3));
    setDisplayIndex(-1);
    setTestPhase("presenting");
    generateNewKeypadArrangement();
  }, [generateNewKeypadArrangement]);

  const startNewTrial = useCallback(
    (level: number) => {
      setUserSequenceInput([]);
      setSequenceToRemember(generateRandomSequence(level));
                        setDisplayIndex(-1);
      setTestPhase("presenting");
      generateNewKeypadArrangement();
    },
    [generateNewKeypadArrangement]
  );

  // 👉 handleKeyPress는 입력만 추가
  const handleKeyPress = (digit: number) => {
    if (testPhase !== "input") return;
    setIsAnimatingInput(true);
    setUserSequenceInput((prev) => [...prev, digit]);
    setTimeout(() => setIsAnimatingInput(false), 500);
  };

  const saveResultsToFirebase = async () => {
    try {
      const userId = getUserId();
      const testSetId = getCognitiveTestSetId();

      if (!userId || !testSetId) {
        console.error("사용자 ID 또는 테스트 셋 ID를 찾을 수 없습니다.");
        return;
      }

      const docId = `${userId}_${Date.now()}_${uuidv4()}`;
      const responseRef = doc(db, "userCognitiveResponse", docId);

      await setDoc(responseRef, {
        userId,
        testSetId,
        testType: "test1",
        timestamp: Timestamp.now(),
        results: {
          totalScore: totalSuccess,
          finalSpanLength: finalSpanLength,
        },
      });

      console.log("DigitSpan 결과 저장됨 (Firebase ID):", docId);
    } catch (error) {
      console.error("DigitSpan 결과 저장 오류:", error);
    }
  };

  useLayoutEffect(() => {
    document.documentElement.style.overflow = 'hidden'; // 잠시 스크롤 막음 (깜빡임 방지)
    document.body.style.overflow = 'hidden';
    
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;

    // 0.1초 뒤에 스크롤 다시 품 (자연스럽게)
    const timer = setTimeout(() => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // 👉 userSequenceInput 길이 감시 → 결과 처리
  useEffect(() => {
    if (
      userSequenceInput.length === sequenceToRemember.length &&
      testPhase === "input"
    ) {
      const isCorrect = userSequenceInput.every(
        (val, idx) => val === sequenceToRemember[idx]
      );

      setResults((prev) => [
        ...prev,
        {
          sequence: sequenceToRemember,
          userInput: userSequenceInput,
          correct: isCorrect,
          mode: currentMode,
        },
      ]);
      if (isCorrect) {
        setTotalSuccess((prev) => {
          console.log("totalSuccess", prev);
          return prev + 1;
        });
      }

      if (trialInLevel === 1) {
        const correctCount =
          results.filter(
            (r) =>
              r.mode === currentMode &&
              r.correct &&
              r.sequence.length === currentLevel
          ).length + (isCorrect ? 1 : 0);
        if (correctCount === 0) {
          setFinalSpanLength(currentLevel);
          setTestPhase("finished");
          saveResultsToFirebase();
    } else {
          if (currentLevel < 8) {
            setCurrentLevel(currentLevel + 1);
            setTrialInLevel(0);
            startNewTrial(currentLevel + 1);
          } else {
            setFinalSpanLength(8);
            setTestPhase("finished");
            saveResultsToFirebase();
          }
        }
      } else {
        setTrialInLevel(1);
        startNewTrial(currentLevel);
      }
    }
  }, [
    userSequenceInput,
    testPhase,
    currentLevel,
    trialInLevel,
    sequenceToRemember,
    currentMode,
    results,
    startNewTrial,
  ]);

  useEffect(() => {
    generateRandomColors();
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (
      testPhase === "presenting" &&
      displayIndex < sequenceToRemember.length - 1
    ) {
      setIsDisplayingDigit(true);
      timer = setTimeout(() => {
        setIsDisplayingDigit(false);
        setTimeout(() => setDisplayIndex((prev) => prev + 1), 500);
      }, PRESENTATION_TIME);
    } else if (
      testPhase === "presenting" &&
      displayIndex === sequenceToRemember.length - 1
    ) {
      setIsDisplayingDigit(true);
      timer = setTimeout(() => {
        setIsDisplayingDigit(false);
        setTimeout(() => setTestPhase("input"), 500);
      }, PRESENTATION_TIME);
    }
    return () => clearTimeout(timer);
  }, [testPhase, displayIndex, sequenceToRemember.length]);

  // --- Renders ---
  const renderNumberPad = () => (
    <div className={styles.keypad}>
      {currentKeypadArrangement.map((digit, index) => (
        <button
          key={digit}
          onClick={() => handleKeyPress(digit)}
          className={styles.keypadButton}
          style={{
            backgroundColor:
              currentKeypadColors[index % currentKeypadColors.length],
          }}
        >
          {digit}
        </button>
      ))}
    </div>
  );

  const renderInstructionNumberPad = () => (
    <div className={styles.numberPadContainer}>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit, index) => (
        <button
          key={digit}
          className={styles.numberButton}
          style={{
            backgroundColor:
              currentKeypadColors[index % currentKeypadColors.length],
          }}
        >
          {digit}
        </button>
      ))}
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <ArrowBack />
        </button>
        <span className={styles.headerText}>{t("check_before_3")}</span>
                </div>

      {testPhase === "idle" && (
        <React.Fragment>
          <div className={styles.content}>
            <p className={styles.digitSpanInstructions1}>{t('digit_span_instructions_1')}</p>
            <img
              src="/images/Screenshot 2025-04-29 at 6.19.55 PM.png"
              alt="Instructions"
              className={styles.instructionImage}
            />
            <p className={styles.digitSpanInstructions2}>{t('digit_span_instructions_2')}</p>
            {renderInstructionNumberPad()}
          </div>
          <button onClick={startNewTest} className={styles.startButton}>
              {t("start_test_button")}
            </button>
        </React.Fragment>
                )}

      {(testPhase === "presenting" || testPhase === "input") && (
        <div className={styles.testScreen}>
          <div className={styles.testInfo}>
            <div className={styles.digitLevel}>
              {t("span")} {currentLevel}
            </div>
            <div className={styles.trialCount}>
              {t("trial")} {trialInLevel + 1} / 2
            </div>
          </div>
          {testPhase === "presenting" && displayIndex !== -1 && (
            <div
              className={styles.digitDisplay}
              style={{ opacity: isDisplayingDigit ? 1 : 0 }}
            >
              <span className={styles.digitText}>
                {sequenceToRemember[displayIndex]}
              </span>
            </div>
          )}
          {testPhase === "input" && (
            <>
              <div
                className={`${styles.userInputDisplay} ${
                  isAnimatingInput ? styles.hasInput : ""
                }`}
              >
                <span className={styles.digitText}>
                  {userSequenceInput[userSequenceInput.length - 1] || "\u00A0"}
                </span>
              </div>
              {renderNumberPad()}
            </>
          )}
            </div>
        )}

      {testPhase === "finished" && (
        <React.Fragment>
        <div className={styles.resultsScreen}>
          <h2>{t('test_finished')}</h2>
          <div className={styles.resultsSummary}>
            <p>{t('final_span_length')}: {finalSpanLength}</p>
            <p>{t('total_success')}: {totalSuccess}</p>
          </div>
        </div>
        <button
          onClick={() => navigate(`${prefix}/cognitive/check/new`)}
          className={styles.finishButton}
        >
          {t('finish_button')}
        </button>
        </React.Fragment>
      )}
    </div>
  );
};

export default DigitSpanTest;
